import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

// A 4xx is the server's final answer (expired session, bad request, not found),
// so retrying only repeats it. Network errors and 5xx get one more try.
const isClientError = (error: unknown) => {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    return status !== undefined && status >= 400 && status < 500;
};

// One client for the whole app. Auth failures are not handled here: the axios
// interceptor in api.ts owns the 401 → /login redirect, and queries just see
// the rejected promise.
export const queryClient = new QueryClient({
    // Pages used to log their own fetch failures from a .catch(); log them once
    // here so a failed query is never silent.
    queryCache: new QueryCache({
        onError: (error, query) => console.error(`Query ${JSON.stringify(query.queryKey)} failed:`, error),
    }),
    // Same for mutations. Callers still show their own message (a snackbar); this
    // makes sure the underlying error is in the console too.
    mutationCache: new MutationCache({
        onError: (error) => console.error('Mutation failed:', error),
    }),
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => failureCount < 1 && !isClientError(error),
        },
    },
});
